import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { formatTimeAMPM } from "../../lib/utils";
import { getOrgTimezone, formatDateTime, getTemporalStatus as getTemporalStatusTZ } from "../../lib/timezone";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Separator } from "../../components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronDown,
  Phone, MessageSquare, CheckCircle2, Clock, AlertCircle,
  X, Plus, Edit2, ExternalLink, List, Search, Eye, User, Filter, Trash2,
} from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isToday } from "date-fns";
import { WhatsApp } from "../../components/ui/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

const PRIORITY_COLORS = {
  High: "bg-red-500/15 text-red-500 border-red-500/20",
  Medium: "bg-amber-500/15 text-amber-500 border-amber-500/20",
  Low: "bg-blue-500/15 text-blue-500 border-blue-500/20",
};

const STATUS_COLORS = {
  New: "bg-blue-500/15 text-blue-500 border-blue-500/20",
  Contacted: "bg-yellow-500/15 text-yellow-500 border-yellow-500/20",
  Interested: "bg-green-500/15 text-green-500 border-green-500/20",
  "Not Interested": "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  "Not Reachable": "bg-orange-500/15 text-orange-500 border-orange-500/20",
  "Need Followup": "bg-purple-500/15 text-purple-500 border-purple-500/20",
  Converted: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
  Closed: "bg-zinc-700/15 text-zinc-500 border-zinc-700/20",
};

const TEMPORAL_STATUS_COLORS = {
  Upcoming: "bg-cyan-500/15 text-cyan-500 border-cyan-500/20",
  Overdue: "bg-red-500/15 text-red-500 border-red-500/20",
  Closed: "bg-zinc-500/15 text-zinc-500 border-zinc-500/20",
};

const LEAD_STATUSES = ["New", "Contacted", "Interested", "Not Interested", "Not Reachable", "Need Followup", "Converted", "Closed"];
const STATUS_OPTIONS = [
  { value: "Open", label: "Open" },
  { value: "Closed", label: "Closed" },
  { value: "Need Followup", label: "Need Followup" },
];
const LEAD_PRIORITIES = ["High", "Medium", "Low"];
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const EMPTY_FILTERS = {
  search: "", status: "", priority: "", groupId: "", assignedStaffId: "",
};

function getTemporalStatus(f, timezone) {
  if (!f || f.status === "Closed") return "Closed";
  return getTemporalStatusTZ(f, timezone);
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

function activeFilterCount(filters) {
  return Object.values(filters).filter(v =>
    Array.isArray(v) ? v.length > 0 : !!v
  ).length;
}

const parseMonthParam = (param) => {
  if (!param) return null;
  const parts = param.split("-");
  if (parts.length === 2) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
      return new Date(year, month - 1, 1);
    }
  }
  return null;
};

const parseDateParam = (param) => {
  if (!param) return null;
  const parts = param.split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return new Date(year, month - 1, day);
    }
  }
  return null;
};

// ── Filter Drawer ────────────────────────────────────────────────────────────────────────
function FiltersDrawer({ open, onOpenChange, filters, onChange, onClear, groups, staffList }) {
  const isDesktop = useIsDesktop();
  const [draft, setDraft] = useState(filters);
  useEffect(() => { if (open) setDraft(filters); }, [open, filters]);

  const handleApply = () => { onChange(draft); onOpenChange(false); };
  const handleClear = () => { setDraft(EMPTY_FILTERS); onClear(); onOpenChange(false); };
  const set = (key, val) => setDraft(prev => ({ ...prev, [key]: val }));

  const draftCount = activeFilterCount(draft);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isDesktop ? "right" : "bottom"}
        className={isDesktop
          ? "w-[400px] flex flex-col p-0 bg-card border-border"
          : "rounded-t-2xl flex flex-col p-0 max-h-[92vh] bg-card border-border"
        }
      >
        <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
              <Filter className="h-4 w-4" /> Filters
              {draftCount > 0 && (
                <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5">{draftCount} active</Badge>
              )}
            </SheetTitle>
            {draftCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleClear} className="text-muted-foreground text-xs h-7">
                <X className="h-3.5 w-3.5 mr-1" />Clear all
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Lead name / phone..."
                value={draft.search}
                onChange={(e) => set("search", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
                className="pl-9"
              />
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Group</Label>
              <Select value={draft.groupId || "all"} onValueChange={(v) => set("groupId", v === "all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="All Groups" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Groups</SelectItem>
                  {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Assigned Staff</Label>
              <Select value={draft.assignedStaffId || "all"} onValueChange={(v) => set("assignedStaffId", v === "all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="All Staff" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Priority</p>
            <div className="flex flex-wrap gap-2">
              {LEAD_PRIORITIES.map(p => (
                <button key={p} type="button"
                  onClick={() => set("priority", draft.priority === p ? "" : p)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-mono transition-all ${draft.priority === p
                    ? PRIORITY_COLORS[p] + " border-current"
                    : "border-border text-muted-foreground hover:bg-secondary/50"
                    }`}>{p}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Lead Status</p>
            <div className="flex flex-wrap gap-2">
              {LEAD_STATUSES.map(s => (
                <button key={s} type="button"
                  onClick={() => set("status", draft.status === s ? "" : s)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-mono transition-all ${draft.status === s
                    ? STATUS_COLORS[s] + " border-current"
                    : "border-border text-muted-foreground hover:bg-secondary/50"
                    }`}>{s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-card shrink-0">
          <Button className="w-full font-heading uppercase tracking-wide text-xs" onClick={handleApply}>
            <Search className="h-4 w-4 mr-2" />Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Mobile Follow-up Card ──────────────────────────────────────────────────────────────────────────────────────
function FollowupMobileCard({ f, expanded, onToggle, onView, tz, onDelete }) {
  return (
    <Collapsible open={expanded} onOpenChange={onToggle} asChild>
      <div className="border-border border-b hover:bg-secondary/5 transition-colors">
        <CollapsibleTrigger asChild>
          <div className="cursor-pointer select-none">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0 p-2">
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">{f.leadName?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate leading-tight">{f.leadName}</p>
                  {f.leadPhone && (
                    <p className="text-[11px] text-muted-foreground font-mono flex items-center gap-1 mt-0.5">
                      <Phone className="h-2.5 w-2.5" />{f.leadPhone}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 pr-1">
                <div className="flex flex-col items-end gap-1">
                  <span className="font-mono text-[10px] tracking-tighter flex items-center gap-1"><CalendarIcon className="h-3 w-3 text-muted-foreground" />{f.scheduledDate}</span>
                  {f.scheduledTime && <span className="font-mono text-[10px] tracking-tighter text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{formatTimeAMPM(f.scheduledTime)}</span>}
                </div>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
              </div>
            </div>
            <div className="flex items-center gap-1 mb-2 ml-3">
              {f.status !== "Closed" && (
                <Badge className={`${TEMPORAL_STATUS_COLORS[getTemporalStatus(f, tz)]} border text-[9px] px-1 py-0 h-4 font-mono uppercase`}>{getTemporalStatus(f, tz)}</Badge>
              )}
              <Badge className={`${STATUS_COLORS[f.leadStatus] || STATUS_COLORS.New} border text-[9px] px-1 py-0 h-4 font-mono uppercase`}>{f.leadStatus}</Badge>
              <Badge className={`${PRIORITY_COLORS[f.leadPriority] || PRIORITY_COLORS.Medium} border text-[9px] px-1 py-0 h-4 font-mono uppercase`}>{f.leadPriority}</Badge>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-3 border-t border-border/40">
            {f.leadPhone && (
              <div className="grid grid-cols-2 gap-2 p-2 border-b border-border">
                <a
                  href={`tel:${f.leadPhone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-sky-400 dark:hover:border-sky-500 hover:shadow-md active:scale-95 px-2"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 dark:bg-sky-950/50 transition-colors group-hover:bg-sky-100 dark:group-hover:bg-sky-900/50">
                    <Phone className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                  </div>
                  <div className="flex flex-col items-start leading-tight min-w-0">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                      Mobile Call
                    </span>
                    <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                      Call Now
                    </span>
                  </div>
                </a>
                <a
                  href={`https://wa.me/${f.leadPhone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md active:scale-95 px-2"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/50 transition-colors group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50">
                    <WhatsApp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="flex flex-col items-start leading-tight min-w-0">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                      Message
                    </span>
                    <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                      WhatsApp
                    </span>
                  </div>
                </a>
              </div>
            )}
            <div className="flex items-center gap-2 p-2 border-b border-border/30">
              <Button variant="outline" size="sm" className="flex-1 h-8 text-xs gap-1" onClick={(e) => { e.stopPropagation(); onView(f); }}>
                <Eye className="h-3 w-3" /> View Details & Notes
              </Button>
              <Button variant="destructive" size="sm" className="flex-1 h-8 text-xs gap-1" onClick={(e) => { e.stopPropagation(); onDelete(f); }}>
                <Trash2 className="h-3 w-3" /> Delete
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs font-mono text-muted-foreground p-2">
              <div>
                <p className="text-[10px] font-sans font-semibold text-foreground mb-1 uppercase tracking-wider">Scheduled</p>
                <span className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" />{f.scheduledDate}</span>
                {f.scheduledTime && <span className="flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" />{formatTimeAMPM(f.scheduledTime)}</span>}
              </div>
              <div>
                <p className="text-[10px] font-sans font-semibold text-foreground mb-1 uppercase tracking-wider">Assigned Staff</p>
                <span className="text-foreground font-sans font-medium">{f.assignedStaffName || "Not assigned"}</span>
              </div>
            </div>

            <div className="space-y-1 p-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Notes</p>
              <p className="text-xs text-muted-foreground leading-normal mt-0.5 whitespace-pre-wrap">{f.notes || "No notes provided."}</p>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────────────────────────────
export default function LeadFollowups() {
  const { currentBranch, organization, loading } = useAuth();
  const orgTimezone = getOrgTimezone(organization);

  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "calendar";

  const setActiveTab = (val) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", val);
    setSearchParams(nextParams);
  };

  const handleMonthChange = (newMonth) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("month", format(newMonth, "yyyy-MM"));
    setSearchParams(nextParams);
  };

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteFollowup, setDeleteFollowup] = useState(null);

  const [calLoading, setCalLoading] = useState(true);
  const [calendarData, setCalendarData] = useState({});
  const monthStr = useMemo(() => {
    const mParam = searchParams.get("month");
    if (mParam && parseMonthParam(mParam)) {
      return mParam;
    }
    const dParam = searchParams.get("date");
    const parsedD = parseDateParam(dParam);
    if (parsedD) {
      return format(parsedD, "yyyy-MM");
    }
    return format(new Date(), "yyyy-MM");
  }, [searchParams]);

  const currentMonth = useMemo(() => {
    return parseMonthParam(monthStr);
  }, [monthStr]);
  const [selectedDay, setSelectedDay] = useState(null);

  const [listLoading, setListLoading] = useState(false);
  const [listData, setListData] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(20);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedCalRowId, setExpandedCalRowId] = useState(null);

  const toggleRow = (id) => setExpandedRowId(prev => prev === id ? null : id);
  const toggleCalRow = (id) => setExpandedCalRowId(prev => prev === id ? null : id);

  useEffect(() => {
    if (loading) return;
    const fetchInitialData = async () => {
      try {
        const params = new URLSearchParams();
        if (currentBranch?.id) params.append("branchId", currentBranch.id);
        const [groupsRes, staffRes] = await Promise.all([
          api.get(`/lead-management/groups?${params}`),
          api.get(`/lead-management/staff/list?${params}`),
        ]);
        setGroups(groupsRes.data.groups || []);
        setStaffList(staffRes.data.staff || []);
      } catch (err) { console.error(err); }
    };
    fetchInitialData();
  }, [currentBranch, loading]);

  const monthStats = useMemo(() => {
    const stats = { od: 0, up: 0, cd: 0 };
    Object.values(calendarData).forEach(dayData => {
      if (dayData.followups) {
        dayData.followups.forEach(f => {
          const ts = getTemporalStatus(f, orgTimezone);
          if (ts === "Overdue") stats.od++;
          else if (ts === "Upcoming") stats.up++;
          else if (ts === "Closed") stats.cd++;
        });
      }
    });
    return stats;
  }, [calendarData, orgTimezone]);

  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      if (filters.groupId) params.append("groupId", filters.groupId);
      if (filters.assignedStaffId) params.append("assignedStaffId", filters.assignedStaffId);

      const res = await api.get(`/lead-management/followups/stats?${params.toString()}`);
      setStats(res.data);
    } catch { }
  }, [filters, currentBranch]);

  useEffect(() => {
    if (loading || currentBranch === undefined) return;
    fetchStats();
  }, [fetchStats, currentBranch, loading]);

  const fetchCalendarData = useCallback(async () => {
    setCalLoading(true);
    try {
      const month = format(currentMonth, "yyyy-MM");
      const params = new URLSearchParams();
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      if (filters.groupId) params.append("groupId", filters.groupId);
      if (filters.assignedStaffId) params.append("assignedStaffId", filters.assignedStaffId);
      params.append("month", month);

      const res = await api.get(`/lead-management/followups/calendar?${params.toString()}`);
      setCalendarData(res.data.calendar || {});
    } catch (err) { console.error(err); }
    finally { setCalLoading(false); }
  }, [currentMonth, currentBranch, filters]);

  useEffect(() => {
    if (loading || currentBranch === undefined) return;
    fetchCalendarData();
  }, [fetchCalendarData, currentBranch, loading]);

  useEffect(() => {
    const dParam = searchParams.get("date");
    const parsedD = parseDateParam(dParam);
    if (parsedD) {
      const dateStr = format(parsedD, "yyyy-MM-dd");
      const dayData = calendarData[dateStr] || { total: 0, high: 0, medium: 0, low: 0, followups: [] };
      setSelectedDay({ date: parsedD, data: dayData });
    } else if (calendarData && Object.keys(calendarData).length > 0) {
      if (selectedDay) {
        const selStr = format(selectedDay.date, "yyyy-MM-dd");
        const updatedData = calendarData[selStr] || { total: 0, high: 0, medium: 0, low: 0, followups: [] };
        setSelectedDay({ date: selectedDay.date, data: updatedData });
      } else {
        const today = new Date();
        const dateStr = format(today, "yyyy-MM-dd");
        const dayData = calendarData[dateStr] || { total: 0, high: 0, medium: 0, low: 0, followups: [] };
        setSelectedDay({ date: today, data: dayData });
      }
    } else if (!selectedDay) {
      const today = new Date();
      setSelectedDay({ date: today, data: { total: 0, high: 0, medium: 0, low: 0, followups: [] } });
    }
  }, [calendarData, searchParams]);

  const fetchListData = useCallback(async () => {
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      if (filters.groupId) params.append("groupId", filters.groupId);
      if (filters.assignedStaffId) params.append("assignedStaffId", filters.assignedStaffId);
      params.append("page", listPage);
      params.append("limit", listPageSize);

      const res = await api.get(`/lead-management/followups?${params.toString()}`);
      setListData(res.data.followups || []);
      setListTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setListLoading(false); }
  }, [filters, currentBranch, listPage, listPageSize]);

  useEffect(() => { setListPage(1); }, [filters, listPageSize, currentBranch]);
  useEffect(() => {
    if (loading || currentBranch === undefined) return;
    if (activeTab === "list") {
      fetchListData();
    }
  }, [fetchListData, activeTab, currentBranch, loading]);

  const refreshAll = useCallback(() => {
    fetchCalendarData();
    fetchStats();
    if (activeTab === "list") {
      fetchListData();
    }
  }, [fetchCalendarData, fetchStats, fetchListData, activeTab]);

  const handleViewLead = async (followup) => {
    try {
      const res = await api.get(`/lead-management/${followup.leadId}`);
      setSelectedLead({
        ...res.data.lead,
        followups: res.data.followups,
        activities: res.data.activities,
        currentFollowup: followup,
      });
      setShowDetailsModal(true);
    } catch { toast.error("Failed to load lead details"); }
  };

  const handleDayClick = (day) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("date", format(day, "yyyy-MM-dd"));
    nextParams.set("month", format(day, "yyyy-MM"));
    setSearchParams(nextParams);
  };

  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const filterCount = activeFilterCount(filters);
  const totalPages = Math.max(1, Math.ceil(listTotal / listPageSize));

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-1.5 mt-1 md:space-y-3" data-testid="lead-followups-page">

      {/* ── Header ── */}
      <div className="flex mt-1 flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-black font-heading tracking-tight uppercase">Lead Follow-ups</h1>
          <p className="text-xs tracking-tight text-muted-foreground">Manage and track lead follow-ups</p>
        </div>
        <Button
          variant={filterCount > 0 ? "secondary" : "outline"}
          size="sm"
          onClick={() => setShowFiltersDrawer(true)}
          className="font-heading uppercase tracking-wide text-xs self-start sm:self-auto"
          data-testid="toggle-filters-btn"
        >
          <Filter className="h-4 w-4 mr-2" />Filters
          {filterCount > 0 && (
            <Badge className="ml-1.5 bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px]">{filterCount}</Badge>
          )}
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="flex overflow-x-auto gap-2 pb-1 -mx-4 px-4 md:grid md:grid-cols-5 md:gap-3 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            { label: "Total", value: stats.total, color: "" },
            { label: "Today", value: stats.todayCount, color: "text-blue-500" },
            { label: "Overdue", value: stats.overdueCount, color: "text-red-500" },
            { label: "Scheduled", value: stats.scheduled, color: "text-orange-500" },
            { label: "Completed", value: stats.completed, color: "text-emerald-500" },
          ].map(s => (
            <Card key={s.label} className="bg-card border-border min-w-[100px] md:min-w-0 shrink-0">
              <CardContent className="p-2 text-center">
                <p className={`text-xl font-black font-heading ${s.color}`}>{s.value}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 space-y-2 md:space-y-4">
        <TabsList className="bg-secondary/50 p-1 justify-start">
          <TabsTrigger value="calendar" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-calendar">
            <CalendarIcon className="h-3.5 w-3.5 mr-2" />Calendar
          </TabsTrigger>
          <TabsTrigger value="list" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-list">
            <List className="h-3.5 w-3.5 mr-2" />List
          </TabsTrigger>
        </TabsList>

        {/* ══ CALENDAR TAB ════════════════════════════════════════════════════════════════════════════ */}
        <TabsContent value="calendar">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-start">
            {/* Left: Calendar Card */}
            <Card className="lg:col-span-5 bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 py-2">
                <div className="flex flex-col gap-1">
                  <CardTitle className="text-sm font-heading uppercase tracking-wide leading-none">Calendar</CardTitle>
                  <div className="flex items-center gap-2 text-[10px] font-mono font-bold leading-none">
                    {monthStats.od > 0 && <span className="text-red-500">OD: {monthStats.od}</span>}
                    {monthStats.up > 0 && <span className="text-cyan-500">UP: {monthStats.up}</span>}
                    {monthStats.cd > 0 && <span className="text-zinc-500">CD: {monthStats.cd}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleMonthChange(subMonths(currentMonth, 1))} data-testid="prev-month-btn"><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-sm font-medium min-w-[100px] text-center">{format(currentMonth, "MMMM yyyy")}</span>
                  <Button variant="ghost" size="icon" onClick={() => handleMonthChange(addMonths(currentMonth, 1))} data-testid="next-month-btn"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </CardHeader>
              <CardContent className="p-1">
                {calLoading ? (
                  <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>
                ) : (
                  <>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {DAYS_OF_WEEK.map(d => (
                        <div key={d.key} className="text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground py-2">{d.label}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                      {eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) }).map((day, idx) => {
                        const dayOfWeek = day.getDay();
                        const isFirstDay = idx === 0;
                        const dateStr = format(day, "yyyy-MM-dd");
                        const dayData = calendarData[dateStr];
                        const hasFollowups = dayData?.total > 0;
                        const today = isToday(day);
                        const isSelected = selectedDay && format(selectedDay.date, "yyyy-MM-dd") === dateStr;

                        const counts = hasFollowups ? dayData.followups?.reduce((acc, f) => {
                          const ts = getTemporalStatus(f, orgTimezone);
                          if (ts === "Overdue") acc.od++;
                          else if (ts === "Upcoming") acc.up++;
                          else if (ts === "Closed") acc.cd++;
                          return acc;
                        }, { od: 0, up: 0, cd: 0 }) : null;

                        return (
                          <div key={day.toISOString()}
                            style={{ gridColumn: isFirstDay ? dayOfWeek + 1 : undefined }}
                            onClick={() => handleDayClick(day)}
                            className={`min-h-[60px] sm:min-h-[80px] p-1 sm:p-2 rounded-sm border cursor-pointer transition-all ${today ? "border-primary bg-primary/5 shadow-[0_0_10px_rgba(var(--primary),0.1)]"
                              : isSelected ? "border-primary bg-primary/5"
                                : hasFollowups ? "border-border bg-secondary/30 hover:bg-secondary/50"
                                  : "border-border/50 bg-background hover:bg-secondary/20"
                              }`} data-testid={`calendar-day-${dateStr}`}
                          >
                            <div className={`text-[10px] font-medium mb-1 flex justify-between ${today ? "text-primary" : ""}`}><span>{format(day, "d")}</span>{counts && (
                              <span className={`text-[9px] font-medium w-4 h-4 flex items-center justify-center rounded-full ${counts.od > 0 ? "bg-red-500 text-white" : ""} ${counts.up > 0 ? "bg-blue-500 text-white" : ""} ${counts.cd > 0 ? "bg-green-500 text-white" : ""}`}>{dayData.total}</span>
                            )}</div>
                            {hasFollowups && (
                              <div className="space-y-1">
                                <div className="flex gap-1 flex-wrap">
                                  {dayData.high > 0 && <div className="h-1.5 w-1.5 rounded-full bg-red-500" title={`${dayData.high} High`} />}
                                  {dayData.medium > 0 && <div className="h-1.5 w-1.5 rounded-full bg-amber-500" title={`${dayData.medium} Medium`} />}
                                  {dayData.low > 0 && <div className="h-1.5 w-1.5 rounded-full bg-blue-500" title={`${dayData.low} Low`} />}
                                </div>
                                {counts && (
                                  <div className="flex flex-wrap gap-x-1.5 gap-y-0 text-[9px] font-mono font-bold leading-none mt-1">
                                    {counts.od > 0 && <span className="text-red-500">OD:{counts.od}</span>}
                                    {counts.up > 0 && <span className="text-cyan-500">UP:{counts.up}</span>}
                                    {counts.cd > 0 && <span className="text-zinc-500">CD:{counts.cd}</span>}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-center gap-x-4 gap-y-2 sm:gap-x-6 mt-4 pt-4 border-t border-border flex-wrap">
                      {[
                        { color: "bg-red-500", label: "High" },
                        { color: "bg-amber-500", label: "Medium" },
                        { color: "bg-blue-500", label: "Low" },
                        { color: "text-red-500", label: "OD", desc: "Overdue", type: "text" },
                        { color: "text-cyan-500", label: "UP", desc: "Upcoming", type: "text" },
                        { color: "text-zinc-500", label: "CD", desc: "Closed", type: "text" },
                      ].map(l => (
                        <div key={l.label} className="flex items-center gap-1.5">
                          {l.type === "text" ? (
                            <span className={`text-[9px] font-mono font-bold ${l.color}`}>{l.label}</span>
                          ) : (
                            <div className={`w-2.5 h-2.5 rounded-full ${l.color}`} />
                          )}
                          <span className="text-[11px] text-muted-foreground">{l.desc || l.label}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Right: Day Details Card */}
            <Card className="lg:col-span-7 bg-card border-border flex flex-col h-full min-h-[500px] lg:h-[calc(100vh-280px)]">
              <CardHeader className="px-3 py-2 border-b border-border shrink-0">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-primary" />
                  <CardTitle className="text-sm font-heading uppercase tracking-wide">
                    {selectedDay ? format(selectedDay.date, "EEEE, MMM d, yyyy") : "Daily Details"}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-0 scroll-smooth">
                {!selectedDay ? (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground opacity-60">
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-3">
                      <CalendarIcon className="h-6 w-6" />
                    </div>
                    <p className="text-sm font-medium">Select a day from the calendar</p>
                    <p className="text-xs mt-1">to view scheduled follow-ups</p>
                  </div>
                ) : selectedDay.data.total === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground opacity-60">
                    <MessageSquare className="h-10 w-10 mb-3" />
                    <p className="text-sm">No follow-ups scheduled for this day</p>
                  </div>
                ) : (
                  <div className="p-1 md:p-4 space-y-2">
                    {/* Day Summary */}
                    <div className="flex items-center justify-between px-3 py-2 bg-secondary/30 rounded-xl border border-border">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-baseline gap-2">
                          <p className="text-lg font-black font-heading leading-none">{selectedDay.data.total}</p>
                          <p className="text-[10px] text-muted-foreground font-mono uppercase">Total Tasks</p>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(() => {
                            const counts = selectedDay.data.followups.reduce((acc, f) => {
                              const ts = getTemporalStatus(f, orgTimezone);
                              acc[ts] = (acc[ts] || 0) + 1;
                              return acc;
                            }, {});
                            return (
                              <>
                                {counts.Overdue > 0 && <Badge className={`${TEMPORAL_STATUS_COLORS.Overdue} border text-[9px] h-4 px-1.5`}>{counts.Overdue} Overdue</Badge>}
                                {counts.Upcoming > 0 && <Badge className={`${TEMPORAL_STATUS_COLORS.Upcoming} border text-[9px] h-4 px-1.5`}>{counts.Upcoming} Upcoming</Badge>}
                                {counts.Closed > 0 && <Badge className={`${TEMPORAL_STATUS_COLORS.Closed} border text-[9px] h-4 px-1.5`}>{counts.Closed} Closed</Badge>}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <p className="text-[10px] text-muted-foreground font-mono uppercase">Priority</p>
                        <div className="flex gap-1">
                          {selectedDay.data.high > 0 && <Badge className={`${PRIORITY_COLORS.High}   border text-[9px] h-4 px-1`}>H:{selectedDay.data.high}</Badge>}
                          {selectedDay.data.medium > 0 && <Badge className={`${PRIORITY_COLORS.Medium} border text-[9px] h-4 px-1`}>M:{selectedDay.data.medium}</Badge>}
                          {selectedDay.data.low > 0 && <Badge className={`${PRIORITY_COLORS.Low}    border text-[9px] h-4 px-1`}>L:{selectedDay.data.low}</Badge>}
                        </div>
                      </div>
                    </div>

                    {/* Follow-ups List */}
                    <div className="space-y-2">
                      {selectedDay.data.followups.map(followup => {
                        const isExpanded = expandedCalRowId === followup.id;
                        return (
                          <div
                            key={followup.id}
                            className="group bg-card border border-border rounded-xl hover:border-primary/50 transition-colors shadow-sm overflow-hidden"
                            data-testid={`day-followup-${followup.id}`}
                          >
                            <div
                              onClick={() => toggleCalRow(followup.id)}
                              className="px-3 py-2 cursor-pointer flex items-center justify-between gap-3 select-none"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <Avatar className="h-8 w-8 shrink-0 ring-1 ring-border group-hover:ring-primary/30 transition-all">
                                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                                    {followup.leadName?.[0]?.toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-baseline gap-1.5 flex-wrap">
                                    <p className="font-semibold text-sm truncate">{followup.leadName}</p>
                                    <span
                                      className="text-[10px] text-muted-foreground flex items-center gap-0.5 font-mono"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Phone className="h-2.5 w-2.5" />{followup.leadPhone}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2.5 shrink-0">
                                <div className="flex items-end flex-col gap-1">
                                  {followup.scheduledTime && (
                                    <div className="text-[10px] tracking-tight text-muted-foreground flex items-center gap-1 font-mono">
                                      <Clock className="h-2.5 w-2.5" /> <span>{formatTimeAMPM(followup.scheduledTime)}</span>
                                    </div>
                                  )}
                                  <div className="flex gap-1">
                                    <Badge className={`${PRIORITY_COLORS[followup.leadPriority] || PRIORITY_COLORS.Medium} border text-[8px] font-mono uppercase h-3.5 px-1`}>
                                      {followup.leadPriority}
                                    </Badge>
                                    <Badge className={`${STATUS_COLORS[followup.leadStatus] || STATUS_COLORS.New} border text-[8px] font-mono uppercase h-3.5 px-1`}>
                                      {followup.leadStatus}
                                    </Badge>
                                    <Badge className={`${TEMPORAL_STATUS_COLORS[getTemporalStatus(followup, orgTimezone)]} border text-[8px] font-mono uppercase h-3.5 px-1`}>
                                      {getTemporalStatus(followup, orgTimezone)}
                                    </Badge>
                                  </div>
                                </div>
                                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0 ${isExpanded ? "rotate-180" : ""}`} />
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="border-t border-border bg-secondary/15 text-xs ">
                                {followup.leadPhone && (
                                  <div className="grid grid-cols-2 gap-2 p-2 border-b border-border">
                                    <a
                                      href={`tel:${followup.leadPhone}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-sky-400 dark:hover:border-sky-500 hover:shadow-md active:scale-95 px-2"
                                    >
                                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 dark:bg-sky-950/50 transition-colors group-hover:bg-sky-100 dark:group-hover:bg-sky-900/50">
                                        <Phone className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                                      </div>
                                      <div className="flex flex-col items-start leading-tight min-w-0">
                                        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                                          Mobile Call
                                        </span>
                                        <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                                          Call Now
                                        </span>
                                      </div>
                                    </a>
                                    <a
                                      href={`https://wa.me/${followup.leadPhone}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md active:scale-95 px-2"
                                    >
                                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/50 transition-colors group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50">
                                        <WhatsApp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                      </div>
                                      <div className="flex flex-col items-start leading-tight min-w-0">
                                        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                                          Message
                                        </span>
                                        <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                                          WhatsApp
                                        </span>
                                      </div>
                                    </a>
                                  </div>
                                )}
                                <div className="flex items-center justify-end gap-2  border-b border-border/50 bg-secondary/20 p-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-[10px] gap-1 font-heading uppercase tracking-wider"
                                    onClick={(e) => { e.stopPropagation(); handleViewLead(followup); }}
                                  >
                                    <Eye className="h-3 w-3" /> View Details & Notes
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    className="h-7 text-[10px] gap-1 font-heading uppercase tracking-wider"
                                    onClick={(e) => { e.stopPropagation(); setDeleteFollowup(followup); setShowDeleteDialog(true); }}
                                  >
                                    <Trash2 className="h-3 w-3" /> Delete
                                  </Button>
                                </div>
                                <div className="space-y-1 px-3">
                                  <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Notes</p>
                                  <p className="text-muted-foreground leading-normal whitespace-pre-wrap">{followup.notes || "No notes provided."}</p>
                                </div>

                                {followup.assignedStaffName && (
                                  <div className="grid grid-cols-2 gap-2 pt-1.5 border-t border-border/50 px-3 pb-2">
                                    <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Assigned Staff</p>
                                    <p className="font-medium text-foreground mt-0.5">{followup.assignedStaffName}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ══ LIST TAB ════════════════════════════════════════════════════════════════════════════ */}
        <TabsContent value="list" className="flex-1 min-h-0 flex flex-col">
          <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0 md:mx-0 rounded-lg">
            {listLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
              </div>
            ) : listData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                <MessageSquare className="h-10 w-10 mb-3" />
                <p className="text-sm">No follow-ups found</p>
                {filterCount > 0 && (
                  <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={clearFilters}>
                    <X className="h-3.5 w-3.5 mr-1" />Clear filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="md:hidden overflow-y-auto flex-1">
                  {listData.map(f => (
                    <FollowupMobileCard
                      key={f.id}
                      f={f}
                      expanded={expandedRowId === f.id}
                      onToggle={() => toggleRow(f.id)}
                      onView={handleViewLead}
                      onDelete={(followup) => {
                        setDeleteFollowup(followup);
                        setShowDeleteDialog(true);
                      }}
                      tz={orgTimezone}
                    />
                  ))}
                </div>

                <div className="hidden md:block overflow-auto flex-1">
                  <Table>
                    <TableHeader className="sticky top-0 bg-card z-10">
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Lead</TableHead>
                        <TableHead>Scheduled</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Assigned To</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {listData.map(f => (
                        <Fragment key={f.id}>
                          <TableRow
                            className="group cursor-pointer hover:bg-secondary/50"
                            data-testid={`followup-row-${f.id}`}
                            onClick={() => toggleRow(f.id)}
                          >
                            <TableCell className="w-10">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); toggleRow(f.id); }}>
                                <ChevronDown className={`h-4 w-4 transition-transform ${expandedRowId === f.id ? "rotate-180" : ""}`} />
                              </Button>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-9 w-9 border border-border">
                                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading">{f.leadName?.[0]?.toUpperCase()}</AvatarFallback>
                                </Avatar>
                                <div className="cursor-pointer hover:text-primary transition-colors">
                                  <p className="font-medium text-sm leading-tight">{f.leadName}</p>
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    {f.leadPhone || "—"}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-0.5">
                                <span className="font-mono text-sm flex items-center gap-1"><CalendarIcon className="h-3 w-3 text-muted-foreground" />{f.scheduledDate}</span>
                                {f.scheduledTime && <span className="font-mono text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{formatTimeAMPM(f.scheduledTime)}</span>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">{f.notes || "—"}</p>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {f.status !== "Closed" && (
                                  <Badge className={`${TEMPORAL_STATUS_COLORS[getTemporalStatus(f, orgTimezone)]} border text-[10px] font-mono uppercase w-fit`}>{getTemporalStatus(f, orgTimezone)}</Badge>
                                )}
                                <Badge className={`${STATUS_COLORS[f.leadStatus] || STATUS_COLORS.New} border text-[10px] font-mono uppercase w-fit`}>{f.leadStatus}</Badge>
                                <Badge className={`${PRIORITY_COLORS[f.leadPriority] || PRIORITY_COLORS.Medium} border text-[10px] font-mono uppercase w-fit`}>{f.leadPriority}</Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{f.assignedStaffName || <span className="text-muted-foreground">—</span>}</span>
                            </TableCell>
                          </TableRow>
                          {expandedRowId === f.id && (
                            <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border">
                              <TableCell colSpan={6} className="p-0">
                                <div className="flex flex-col animate-in fade-in duration-200">
                                  {f.leadPhone && (
                                    <div className="grid grid-cols-2 gap-2 p-2 border-b border-border">
                                      <a
                                        href={`tel:${f.leadPhone}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-sky-400 dark:hover:border-sky-500 hover:shadow-md active:scale-95 px-2"
                                      >
                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 dark:bg-sky-950/50 transition-colors group-hover:bg-sky-100 dark:group-hover:bg-sky-900/50">
                                          <Phone className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                                        </div>
                                        <div className="flex flex-col items-start leading-tight min-w-0">
                                          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                                            Mobile Call
                                          </span>
                                          <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                                            Call Now
                                          </span>
                                        </div>
                                      </a>
                                      <a
                                        href={`https://wa.me/${f.leadPhone}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md active:scale-95 px-2"
                                      >
                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/50 transition-colors group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50">
                                          <WhatsApp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                        </div>
                                        <div className="flex flex-col items-start leading-tight min-w-0">
                                          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                                            Message
                                          </span>
                                          <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                                            WhatsApp
                                          </span>
                                        </div>
                                      </a>
                                    </div>
                                  )}
                                  <div className="flex items-center justify-end gap-2 p-2 border-b border-border bg-secondary/20">

                                    <div className="flex items-center gap-2">
                                      <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
                                        onClick={(e) => { e.stopPropagation(); handleViewLead(f); }}>
                                        <Eye className="h-3.5 w-3.5" /> <span className="hidden md:block">View Details & Notes</span>
                                      </Button>
                                      <Button variant="destructive" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
                                        onClick={(e) => { e.stopPropagation(); setDeleteFollowup(f); setShowDeleteDialog(true); }}>
                                        <Trash2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Delete</span>
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 text-sm">
                                    <div className="space-y-1.5">
                                      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-1">Notes / Comments</p>
                                      <p className="text-muted-foreground text-xs leading-normal whitespace-pre-wrap">{f.notes || "No notes provided."}</p>
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="font-mono hidden sm:block">{listTotal} total</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs">Show:</span>
                      <Select value={String(listPageSize)} onValueChange={(v) => { setListPageSize(Number(v)); setListPage(1); }}>
                        <SelectTrigger className="w-20 h-8" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={listPage === 1} onClick={() => setListPage(listPage - 1)} data-testid="page-prev-btn">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="sm:text-sm text-xs text-muted-foreground px-2">Page {listPage} of {totalPages || 1}</span>
                    <Button variant="outline" size="sm" disabled={listPage * listPageSize >= listTotal} onClick={() => setListPage(listPage + 1)} data-testid="page-next-btn">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <FiltersDrawer
        open={showFiltersDrawer} onOpenChange={setShowFiltersDrawer}
        filters={filters} onChange={setFilters} onClear={clearFilters}
        groups={groups} staffList={staffList}
      />

      <LeadDetailsModal
        open={showDetailsModal} onOpenChange={setShowDetailsModal}
        lead={selectedLead}
        orgTimezone={orgTimezone}
        onSuccess={refreshAll}
      />

      <DeleteFollowupDialog
        open={showDeleteDialog} onOpenChange={setShowDeleteDialog}
        followup={deleteFollowup}
        onSuccess={refreshAll}
      />
    </div>
  );
}

// ── Delete Confirm Dialog ──────────────────────────────────────────────────────────────────────────────
function DeleteFollowupDialog({ open, onOpenChange, followup, onSuccess }) {
  const [loading, setLoading] = useState(false);
  if (!followup) return null;

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/lead-management/followups/${followup.id}`);
      toast.success("Follow-up deleted");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete follow-up");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />Delete Follow-up
          </DialogTitle>
        </DialogHeader>
        <div className="my-2 p-4 bg-secondary/30 border border-border rounded-xl space-y-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">{followup.leadName?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-sm">{followup.leadName}</p>
              {followup.leadPhone && (
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{followup.leadPhone}</p>
              )}
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground leading-normal whitespace-pre-wrap">{followup.notes || "No notes provided."}</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
            <span className="flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" />{followup.scheduledDate}
              {followup.scheduledTime && ` at ${formatTimeAMPM(followup.scheduledTime)}`}
            </span>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={handleDelete} disabled={loading} data-testid="confirm-delete-btn">
            <Trash2 className="h-4 w-4 mr-2" />{loading ? "Deleting..." : "Delete Follow-up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Lead Details Modal ─────────────────────────────────────────────────────────────────────────────
function LeadDetailsModal({ open, onOpenChange, lead, orgTimezone, onSuccess }) {
  const [newNote, setNewNote] = useState("");
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNotes, setStatusNotes] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [nextTime, setNextTime] = useState("");

  const [showFollowupStatusChange, setShowFollowupStatusChange] = useState(false);
  const [newFollowupStatus, setNewFollowupStatus] = useState("");
  const [followupStatusNotes, setFollowupStatusNotes] = useState("");
  const [nextFollowupDate, setNextFollowupDate] = useState("");
  const [nextFollowupTime, setNextFollowupTime] = useState("");

  const [loading, setLoading] = useState(false);

  if (!lead) return null;

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setLoading(true);
    try {
      await api.post(`/lead-management/${lead.id}/notes`, { notes: newNote.trim() });
      toast.success("Note added");
      setNewNote("");
      onSuccess?.();
    } catch { toast.error("Failed to add note"); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async () => {
    if (!newStatus) { toast.error("Please select a status"); return; }
    if (newStatus === "Need Followup" && (!nextDate || !nextTime)) { toast.error("Next schedule date and time is required"); return; }
    setLoading(true);
    try {
      await api.post(`/lead-management/${lead.id}/status`, {
        status: newStatus, notes: statusNotes,
        nextScheduledDate: nextDate, nextScheduledTime: nextTime,
      });
      toast.success("Status updated");
      setShowStatusChange(false);
      setNewStatus(""); setStatusNotes(""); setNextDate(""); setNextTime("");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update status"); }
    finally { setLoading(false); }
  };

  const handleFollowupStatusChange = async () => {
    if (!newFollowupStatus) { toast.error("Please select a status"); return; }
    if (newFollowupStatus === "Need Followup" && (!nextFollowupDate || !nextFollowupTime)) {
      toast.error("Next schedule date and time is required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        status: newFollowupStatus,
        notes: followupStatusNotes,
      };
      if (newFollowupStatus === "Need Followup") {
        payload.scheduledDate = nextFollowupDate;
        payload.scheduledTime = nextFollowupTime;
      }
      await api.put(`/lead-management/followups/${lead.currentFollowup.id}`, payload);
      toast.success("Followup status updated");
      setShowFollowupStatusChange(false);
      setNewFollowupStatus(""); setFollowupStatusNotes(""); setNextFollowupDate(""); setNextFollowupTime("");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update followup status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] sm:w-full max-h-[85vh] sm:max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden rounded-xl">
        <DialogHeader className="px-4 py-3 sm:px-6 sm:pt-5 sm:pb-3 border-b border-border shrink-0">
          <DialogTitle className="font-heading uppercase tracking-wide text-sm sm:text-base">Lead Details</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5 space-y-4 sm:space-y-6">
          {/* Main Info */}
          <div className="p-3 sm:p-4 bg-secondary/20 rounded-xl space-y-2.5 sm:space-y-3 border border-border/40">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2.5">
              <div>
                <p className="font-heading font-bold text-base sm:text-lg leading-tight">{lead.name}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-muted-foreground mt-1">
                  <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 shrink-0" />{lead.phone}</span>
                  {lead.email && <span className="truncate max-w-[200px] sm:max-w-none">{lead.email}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <Badge className={`${PRIORITY_COLORS[lead.priority]} border text-[9px] sm:text-[10px] font-mono uppercase px-1.5 py-0`}>{lead.priority}</Badge>
                <Badge className={`${STATUS_COLORS[lead.status]}   border text-[9px] sm:text-[10px] font-mono uppercase px-1.5 py-0`}>{lead.status}</Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2.5 sm:gap-4 text-xs sm:text-sm pt-2.5 border-t border-border/50">
              <div>
                <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Source</p>
                <p className="font-medium">{lead.source || "—"}</p>
              </div>
              {lead.interest && (
                <div>
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Interest</p>
                  <p className="font-medium">{lead.interest}</p>
                </div>
              )}
              {lead.reference && (
                <div>
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Reference</p>
                  <p className="font-medium">{lead.reference}</p>
                </div>
              )}
              {lead.groupNames?.length > 0 && (
                <div>
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Groups</p>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {lead.groupNames.map((name, i) => <Badge key={i} variant="secondary" className="text-[9px] px-1 py-0">{name}</Badge>)}
                  </div>
                </div>
              )}
              {lead.assignedStaffNames?.length > 0 && (
                <div className="col-span-2 sm:col-span-1">
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Assigned Staff</p>
                  <p className="font-medium">{lead.assignedStaffNames.join(", ")}</p>
                </div>
              )}
            </div>
            {lead.notes && (
              <div className="pt-2.5 border-t border-border/50">
                <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-0.5">Notes</p>
                <p className="text-xs sm:text-sm text-muted-foreground whitespace-pre-wrap">{lead.notes}</p>
              </div>
            )}
          </div>

          {/* Add Response Note */}
          <div className="space-y-2.5 p-3 sm:p-4 border border-border rounded-xl">
            <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Add Response Note</p>
            <Textarea
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="Enter your followup response or notes..."
              className="text-xs sm:text-sm"
              rows={2}
              data-testid="modal-note-input"
            />
            <Button size="sm" onClick={handleAddNote} disabled={loading || !newNote.trim()} className="text-xs font-heading uppercase tracking-wider" data-testid="modal-add-note-btn">
              <MessageSquare className="h-3.5 w-3.5 mr-1.5" />Add Note
            </Button>
          </div>

          {/* Change Lead Status */}
          <div className="space-y-2.5 p-3 sm:p-4 border border-border rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Change Lead Status</p>
                <p className="text-xs text-muted-foreground mt-0.5">Current: <span className="font-semibold">{lead.status}</span></p>
              </div>
              {!showStatusChange && (
                <Button variant="outline" size="sm" onClick={() => setShowStatusChange(true)} className="h-7 text-xs font-heading uppercase tracking-wider" data-testid="modal-change-status-btn">
                  Change Status
                </Button>
              )}
            </div>
            {showStatusChange && (
              <div className="space-y-2.5">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="h-9 text-xs sm:text-sm" data-testid="modal-status-select">
                    <SelectValue placeholder="Select new status" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs sm:text-sm">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {newStatus === "Need Followup" && (
                  <div className="p-3 bg-purple-500/5 border border-purple-500/10 rounded-lg space-y-2.5">
                    <div className="flex items-center gap-1.5 text-purple-500">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      <span className="text-xs font-medium">Schedule Next Followup</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px]">Next Date *</Label>
                        <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} className="h-8 text-xs px-2 mt-0.5" data-testid="modal-next-date-input" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Next Time</Label>
                        <Input type="time" value={nextTime} onChange={e => setNextTime(e.target.value)} className="h-8 text-xs px-2 mt-0.5" data-testid="modal-next-time-input" />
                      </div>
                    </div>
                  </div>
                )}
                <Textarea
                  value={statusNotes}
                  onChange={e => setStatusNotes(e.target.value)}
                  placeholder="Add notes about this status change..."
                  className="text-xs sm:text-sm"
                  rows={2}
                />
                <div className="flex gap-1.5">
                  <Button size="sm" onClick={handleStatusChange} disabled={loading} className="text-xs font-heading uppercase tracking-wider" data-testid="modal-confirm-status-btn">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Update Status
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setShowStatusChange(false); setNewStatus(""); setStatusNotes(""); setNextDate(""); setNextTime(""); }} className="text-xs font-heading uppercase tracking-wider">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Change Follow-up Status */}
          {lead.currentFollowup && (
            <div className="space-y-2.5 p-3 sm:p-4 border border-border rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Change Follow-up Status</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Current: <span className="font-semibold">{lead.currentFollowup.status || "Open"}</span>
                  </p>
                </div>
                {!showFollowupStatusChange && (
                  <Button variant="outline" size="sm" onClick={() => setShowFollowupStatusChange(true)} className="h-7 text-xs font-heading uppercase tracking-wider" data-testid="modal-change-followup-status-btn">
                    Change Follow-up
                  </Button>
                )}
              </div>
              {showFollowupStatusChange && (
                <div className="space-y-2.5">
                  <Select value={newFollowupStatus} onValueChange={setNewFollowupStatus}>
                    <SelectTrigger className="h-9 text-xs sm:text-sm" data-testid="modal-followup-status-select">
                      <SelectValue placeholder="Select followup status" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value} className="text-xs sm:text-sm">{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {newFollowupStatus === "Need Followup" && (
                    <div className="p-3 bg-purple-500/5 border border-purple-500/10 rounded-lg space-y-2.5">
                      <div className="flex items-center gap-1.5 text-purple-500">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        <span className="text-xs font-medium">Reschedule Required</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px]">Next Date *</Label>
                          <Input type="date" value={nextFollowupDate} onChange={e => setNextFollowupDate(e.target.value)} className="h-8 text-xs px-2 mt-0.5" data-testid="modal-next-followup-date-input" />
                        </div>
                        <div>
                          <Label className="text-[10px]">Next Time</Label>
                          <Input type="time" value={nextFollowupTime} onChange={e => setNextFollowupTime(e.target.value)} className="h-8 text-xs px-2 mt-0.5" data-testid="modal-next-followup-time-input" />
                        </div>
                      </div>
                    </div>
                  )}
                  <Textarea
                    value={followupStatusNotes}
                    onChange={e => setFollowupStatusNotes(e.target.value)}
                    placeholder="Add notes about this followup status change..."
                    className="text-xs sm:text-sm"
                    rows={2}
                  />
                  <div className="flex gap-1.5">
                    <Button size="sm" onClick={handleFollowupStatusChange} disabled={loading} className="text-xs font-heading uppercase tracking-wider" data-testid="modal-confirm-followup-status-btn">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Update Followup
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setShowFollowupStatusChange(false); setNewFollowupStatus(""); setFollowupStatusNotes(""); setNextFollowupDate(""); setNextFollowupTime(""); }} className="text-xs font-heading uppercase tracking-wider">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Activity History */}
          <div className="space-y-2.5">
            <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Activity History</p>
            {lead.activities?.length > 0 ? (
              <div className="space-y-2">
                {lead.activities.map(activity => (
                  <div key={activity.id} className="flex gap-2.5 p-2.5 sm:p-3 bg-secondary/15 rounded-lg border border-border/30">
                    <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      {activity.action === "created" && <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />}
                      {activity.action === "updated" && <Edit2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-500" />}
                      {activity.action === "status_changed" && <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-500" />}
                      {activity.action === "note_added" && <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-500" />}
                      {activity.action === "followup_scheduled" && <CalendarIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-500" />}
                      {activity.action === "converted" && <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-500" />}
                      {activity.action === "deleted" && <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
                        <p className="text-xs sm:text-sm font-semibold capitalize text-foreground">{activity.action.replace(/_/g, " ")}</p>
                        <span className="text-[9px] sm:text-[10px] text-muted-foreground font-mono">{formatDateTime(activity.createdAt, orgTimezone)}</span>
                      </div>
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">by {activity.userName}</p>
                      <p className="text-xs sm:text-sm text-foreground/90 mt-1 whitespace-pre-wrap leading-relaxed">{activity.details}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-muted-foreground text-center py-4">No activity recorded</p>
            )}
          </div>
        </div>

        <DialogFooter className="px-4 py-3 sm:px-6 sm:py-4 border-t border-border shrink-0">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="w-full sm:w-auto text-xs font-heading uppercase tracking-wider">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
